import React, { useContext, useEffect, useState } from "react";
import { useAlert } from 'react-alert';
import {
    CardForm,
    CardFormHeading,
    Column,
    ColumnWrapper,
    InfoWrapper,
    InputGroupWrapper,
    ButtonWrapper,
    Disclaimer,
    ErrorViewWrapper
} from "./styled";

import {
    TextInput,
    TextAreaInput,
    EmailInput,
    DateInput,
    CheckboxInput,
    SelectVendor
} from "components";
import { AppContext, withAppContext } from "hocs/withAppContext";
import Layout from "views/Layout";
import PartsForm from "./PartsForm";
import { createPurchaseOrder } from "../service";
import { withEmail } from "hocs/withEmail";


const ErrorView = ({ err }) =>
    <ErrorViewWrapper>
        {err}
    </ErrorViewWrapper>;

const CreatePurchaseOrderRequest = withEmail(({ email }) => {
    const defaultRequestData = {
        requesterEmail: email
    };
    const requiredFieldsMap = {
        'title': true,
        'requesterEmail': true,
        'companyName': true,
        'parts': true,
    };

    const alert = useAlert();
    const { role } = useContext(AppContext);
    const [err, setErr] = useState();
    const [errors, setErrors] = useState({});
    const [request, setRequestData] = useState(defaultRequestData);

    const set = (name, value) => setRequestData({ ...request, [name]: value });

    useEffect(() => {
        if (role && role.enablePurchaseOrderPortal === false) {
            setErr('This portal has not been enabled for public access. Please contact the administrator!');
            return;
        }

        // re-evaluate errors only after the form
        // has been marked dirty, just a better UX
        // this effect can be removed safely
        // it hides the errors as soon as they are fixed
        // it should not run unless user has tried
        // submitting the form at least once
        if (Object.keys(errors).length !== 0) {
            validate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request, role]);

    useEffect(() => {
        // scroll to fault input once the errors have been identified,
        // take one error at the time
        const faultyInput = document.querySelector('[data-error="true"]')
            || document.querySelector('.data-error');
        if (!faultyInput) {
            return;
        }

        const focusedElement = document.activeElement.tagName;
        if (['INPUT', 'TEXTAREA'].indexOf(focusedElement) !== -1) {
            // we want to make sure to not steal focus
            // from any existing element and deteriorate the UX
            return;
        }

        faultyInput.scrollIntoView();
        faultyInput.focus();
    }, [errors]);

    if (err) {
        return <Layout>
            <ErrorView err={err}/>
        </Layout>;
    }

    const [loading, setLoading] = useState(false);
    const validate = () => {
        Object.keys(requiredFieldsMap)
            .map(field => requiredFieldsMap[field]
                && (!request[field] || (Array.isArray(request[field])
                    && request[field].length === 0))
                ? errors[field] = 'This field is required!'
                : delete errors[field]);
        setErrors({ ...errors });

        const isValid = Object.keys(errors).length === 0;
        if (!isValid) {
            // hide loader if the request is not valid
            setLoading(isValid);
        }
        return isValid;
    };

    // use the magic of react,
    // some of our components are not controlled
    // like custom form items,
    // and file uploads
    const [key, setKey] = useState(Math.random().toString(36).substring(2));
    const changeKey = () => setKey(Math.random().toString(36).substring(2));

    const handleOnSubmit = () => {
        setLoading(true);
        if ((Array.isArray(request["parts"]) && request["parts"].length === 0)) {
            alert.error('Please add at least 1 Part to create Purchase Order!');
        }
        validate() && createPurchaseOrder(role.objectId, request)
            .then(() => {
                setRequestData(defaultRequestData);
            })
            .then(() => {
                setLoading(false);
                alert.success('Your request has been submitted!');
                changeKey();
            })
            .catch((err) => {
                setLoading(false);
                alert.error(err);
            })
    };

    const copyCompanyDetails = (checked) => {
        const companyFields = [
            'companyName', // name fields
            'companyStreet', 'companyCity', 'companyState', 'companyZip', // address fields
            'companyPhone', 'companyFax', // contact fields
        ];
        if (!checked) {
            return;
        }

        for (let field of companyFields) {
            if (field === 'companyName') {
                request['shippingCompany'] = request[field];
                continue;
            }

            const fieldSuffix = field.replace('company', '');
            request['shipping' + fieldSuffix] = request[field];
        }
        setRequestData({...request});
    };

    return <Layout key={key}>
        <ColumnWrapper>
            <Column>
                <CardFormHeading>Request Details</CardFormHeading>
                <CardForm>
                    <TextInput
                        label="Title"
                        placeholder="Title to describe the issue"
                        required={requiredFieldsMap['title']}
                        onChange={val => set('title', val)}
                        value={request['title']}
                        error={errors['title']}
                    />

                    <DateInput
                        label="Due Date"
                        placeholder="Enter Due Date"
                        onChange={val => set('dueDate', val)}
                        error={errors['dueDate']}
                    />

                    <SelectVendor
                        label="Vendor"
                        placeholder="Select Vendor"
                        value={request['vendor']}
                        onChange={val => set('vendor', val)}
                        error={errors['vendor']}
                    />

                    <TextAreaInput
                        label="Description"
                        placeholder="Provide some details for this purchase order request"
                        rows="8"
                        onChange={val => set('description', val)}
                        error={errors['description']}
                    />

                    <TextInput
                        label="Requisitioner"
                        placeholder="Requisitioner"
                        onChange={val => set('requisitioner', val)}
                        value={request['requisitioner']}
                        error={errors['requisitioner']}
                    />

                    <TextInput
                        label="Shipping Method"
                        placeholder="Shipping Method"
                        onChange={val => set('shippingMethod', val)}
                        value={request['shippingMethod']}
                        error={errors['shippingMethod']}
                    />

                    <TextInput
                        label="F.O.B Shipping Point"
                        placeholder="F.O.B Shipping Point"
                        onChange={val => set('fobPoint', val)}
                        value={request['fobPoint']}
                        error={errors['fobPoint']}
                    />

                    <TextInput
                        label="Shipping Terms"
                        placeholder="Terms"
                        onChange={val => set('terms', val)}
                        value={request['terms']}
                        error={errors['terms']}
                    />

                    <TextAreaInput
                        label="Notes"
                        placeholder="Add Notes"
                        rows="8"
                        onChange={val => set('notes', val)}
                        error={errors['notes']}
                    />
                </CardForm>
            </Column>
            <Column>
                <CardFormHeading>Requester Information</CardFormHeading>
                <CardForm>
                    <EmailInput
                        label="Email Address"
                        placeholder="Your Email Address"
                        type="email"
                        onChange={val => set('requesterEmail', val)}
                        error={errors['requesterEmail']}
                        required
                    />

                    <InfoWrapper>
                        You can keep track of your requests by logging in to the Portal with the same email address.
                    </InfoWrapper>

                    <TextInput
                        label="Company Name"
                        placeholder="Your Company Name"
                        onChange={val => set('companyName', val)}
                        value={request['companyName']}
                        error={errors['companyName']}
                        required
                    />

                    <TextInput
                        label="Address"
                        placeholder="Street Address"
                        onChange={val => set('companyStreet', val)}
                        value={request['companyStreet']}
                        error={errors['companyStreet']}
                    />

                    <InputGroupWrapper>
                        <TextInput placeholder="City"
                                   onChange={val => set('companyCity', val)}
                                   value={request['companyCity']}
                                    error={errors['companyCity']}
                        />
                        <TextInput placeholder="State"
                                   onChange={val => set('companyState', val)}
                                   value={request['companyState']}
                                   error={errors['companyState']}
                        />
                        <TextInput placeholder="Zip Code"
                                   onChange={val => set('companyZip', val)}
                                   value={request['companyZip']}
                                   error={errors['companyZip']}
                        />
                    </InputGroupWrapper>

                    <InputGroupWrapper>
                        <TextInput
                            label="Phone"
                            placeholder="Phone"
                            onChange={val => set('companyPhone', val)}
                            value={request['companyPhone']}
                            error={errors['companyPhone']}
                        />
                        <TextInput
                            label="Fax"
                            placeholder="Fax Number"
                            onChange={val => set('companyFax', val)}
                            value={request['companyFax']}
                            error={errors['companyFax']}
                        />
                    </InputGroupWrapper>

                </CardForm>

                <CardFormHeading>Shipping Information</CardFormHeading>
                <CardForm>
                    <TextInput
                        label="Ship to Name"
                        placeholder="Name"
                        onChange={val => set('shippingName', val)}
                        value={request['shippingName']}
                        error={errors['shippingName']}
                    />
                    <TextInput
                        label="Company Name"
                        placeholder="Company Name"
                        onChange={val => set('shippingCompany', val)}
                        value={request['shippingCompany']}
                        error={errors['shippingCompany']}
                    />

                    <CheckboxInput
                        label="Use Company Address (Company Name)"
                        onChange={copyCompanyDetails}
                    />

                    <TextInput
                        label="Address"
                        placeholder="Street Address"
                        onChange={val => set('shippingStreet', val)}
                        value={request['shippingStreet']}
                        error={errors['shippingStreet']}
                    />

                    <InputGroupWrapper>
                        <TextInput placeholder="City"
                                   onChange={val => set('shippingCity', val)}
                                   value={request['shippingCity']}
                                   error={errors['shippingCity']}
                        />
                        <TextInput placeholder="State"
                                   onChange={val => set('shippingState', val)}
                                   value={request['shippingState']}
                                   error={errors['shippingState']}
                        />
                        <TextInput placeholder="Zip Code"
                                   onChange={val => set('shippingZip', val)}
                                   value={request['shippingZip']}
                                   error={errors['shippingZip']}
                        />
                    </InputGroupWrapper>

                    <InputGroupWrapper>
                        <TextInput
                            label="Phone"
                            placeholder="Phone"
                            onChange={val => set('shippingPhone', val)}
                            value={request['shippingPhone']}
                            error={errors['shippingPhone']}
                        />
                        <TextInput
                            label="Fax"
                            placeholder="Fax Number"
                            onChange={val => set('shippingFax', val)}
                            value={request['shippingFax']}
                            error={errors['shippingFax']}
                        />
                    </InputGroupWrapper>
                </CardForm>

            </Column>
        </ColumnWrapper>

        <ColumnWrapper>
            <Column>
                <CardFormHeading>Parts</CardFormHeading>
                <PartsForm onChange={(parts, subtotal, shippingCost, otherCost, tax, totalCost) =>{
                    setRequestData({
                        ...request,
                        parts,
                        subtotal,
                        shippingCost,
                        otherCost,
                        tax,
                        totalCost
                    })
                }}/>
            </Column>
        </ColumnWrapper>

        <ColumnWrapper>
            <Column>
                <ButtonWrapper onClick={handleOnSubmit} data-loading={loading}>
                    Submit Purchase Order Request
                </ButtonWrapper>
                <Disclaimer>All requests are sent directly to the company admin.</Disclaimer>
            </Column>
        </ColumnWrapper>
    </Layout>
});


export default withAppContext(CreatePurchaseOrderRequest);
